import React from "react"
import { useSelector } from "react-redux"

import { IDLE } from "const"
import { deepCopy } from "client/utils"
import { StoreType } from "client/store"
import {
    UserIcon,
    DeleteButton,
    Empty,
} from "client/components"
import {
    UserDropdownWrapper,
    UserPreview,
    UserDropdownButton,
    UserDropdownItem,
} from "./styled"


export interface UserDropdownProps {
    selectedId: number
    listIds: readonly number[]
    title: string
    showDelete?: boolean
    showProjectStatus?: boolean
    onChange: (value: number) => void
}

export interface UserDropdownListProps {
    selectedIds: readonly number[]
    protectedIds?: readonly number[]
    listIds: readonly number[]
    title: string
    dropdownDisabled?: boolean
    showProjectStatus?: boolean
    onDelete: (value: number[]) => void
    onAdd: (value: number[]) => void
}

export const UserDropdown = ({
    selectedId,
    listIds,
    title,
    showDelete = true,
    showProjectStatus = true,
    onChange,
}: UserDropdownProps) => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    const selectedUser = userList[selectedId]
    return (
        <UserDropdownWrapper>
            <UserPreview>
                <UserIcon
                    user={selectedUser}
                    showProjectStatus={showProjectStatus}
                    size="sm"
                />
                {
                    selectedUser && showDelete && (
                        <DeleteButton
                            status={IDLE}
                            onClick={() => onChange(null)}
                        />
                    )
                }
            </UserPreview>
            <UserDropdownButton title={title}>
                {
                    listIds.map((userId) => {
                        const user = userList[userId]
                        return userId !== selectedId && user && (
                            <UserDropdownItem
                                key={userId}
                                onClick={() => onChange(userId)}
                            >
                                <UserIcon
                                    user={user}
                                    showProjectStatus={showProjectStatus}
                                    size="sm"
                                />
                            </UserDropdownItem>
                        )
                    })
                }
            </UserDropdownButton>
        </UserDropdownWrapper>
    )
}

export const UserDropdownList = ({
    selectedIds,
    protectedIds = [],
    listIds,
    title,
    dropdownDisabled = false,
    showProjectStatus = true,
    onDelete,
    onAdd,
}: UserDropdownListProps) => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    return (
        <UserDropdownWrapper>
            {
                selectedIds.length === 0 && (
                    <UserPreview>
                        <Empty />
                    </UserPreview>
                )
            }
            {
                selectedIds.map((userId) => {
                    const user = userList[userId]
                    const showDelete = !(protectedIds && protectedIds.includes(userId))
                    return user && (
                        <UserPreview key={userId}>
                            <UserIcon
                                user={user}
                                showProjectStatus={showProjectStatus}
                                size="sm"
                            />
                            {
                                showDelete && (
                                    <DeleteButton
                                        status={IDLE}
                                        onClick={() => onDelete(selectedIds.filter((id) => id !== userId))}
                                    />
                                )
                            }
                        </UserPreview>
                    )
                })
            }
            <UserDropdownButton title={title} disabled={dropdownDisabled}>
                {
                    listIds.map((userId) => {
                        const user = userList[userId]
                        const userSelected = selectedIds && selectedIds.includes(userId)
                        return !userSelected && user && (
                            <UserDropdownItem
                                key={userId}
                                onClick={() => {
                                    const selectedIdsCopy: number[] = deepCopy(selectedIds)
                                    selectedIdsCopy.push(userId)
                                    onAdd(selectedIdsCopy)
                                }}
                            >
                                <UserIcon
                                    user={user}
                                    showProjectStatus={showProjectStatus}
                                    size="sm"
                                />
                            </UserDropdownItem>
                        )
                    })
                }
            </UserDropdownButton>
        </UserDropdownWrapper>
    )
}
